import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useInfra } from '../../data/geo.ts'
import { siteConstraints, useWater } from '../../data/water.ts'

/** The stations a run or a fault may name: the equipment of the irrigation (valves, sprinklers, satellites, controllers) that is still in service, as the course map holds it. */
export interface StationOption { id: string; label: string; areaId: string | null }
export function useStations(): StationOption[] | undefined {
  const infra = useInfra()
  const { i18n } = useTranslation()
  return useMemo(() => infra?.filter((f) => !f.retired_at && ['valve', 'sprinkler', 'satellite', 'controller'].includes(f.kind))
    .map((f) => ({ id: f.id, label: f.code ? `${f.label} (${f.code})` : f.label, areaId: f.area_id }))
    .sort((a, b) => a.label.localeCompare(b.label, i18n.language, { numeric: true })), [infra, i18n.language])
}

/** "12,5" or "12.5" as a number; empty is null; anything else is `invalid` (never guessed). */
export function parseNum(text: string): number | null | 'invalid' {
  const t = text.trim().replace(',', '.')
  if (t === '') return null
  return /^-?\d+(\.\d+)?$/.test(t) ? Number(t) : 'invalid'
}
export const RUN_KINDS = ['program', 'manual', 'test'] as const

export const WATER_TABS = ['advice', 'runs', 'faults', 'settings'] as const
export type WaterTab = (typeof WATER_TABS)[number]
/** Which tab the water page opens on (M23 item 46): the one a link asks for (`?tab=`, Today's "Terrain" row), else the advice for whoever manages the
 *  water (the office), else the runs: the crew's acts are the runs and the faults, the advice is read in the office. */
export function waterTab(search: { tab?: string } | undefined, canManage: boolean): WaterTab {
  const asked = search?.tab
  if (asked && (WATER_TABS as readonly string[]).includes(asked)) return asked as WaterTab
  return canManage ? 'advice' : 'runs'
}
/** The tab on screen (review of M23 item 46): `waterTab` decided once at the first render, when the device had not loaded its permission rows yet
 *  (`can` says false until the mirror answers), so a direction login on a fresh device opened on the runs for good. The default is re-read whenever
 *  the link or the permission changes; a tab chosen by hand holds under the link it was chosen under, and a new link wins over it. */
export function useWaterTab(search: { tab?: string; new?: string } | undefined, canManage: boolean): { tab: WaterTab; choose: (tab: WaterTab) => void } {
  const link = `${search?.tab ?? ''}|${search?.new ?? ''}`
  const [hand, setHand] = useState<{ link: string; tab: WaterTab } | null>(null)
  const tab = hand && hand.link === link ? hand.tab : waterTab(search, canManage)
  return { tab, choose: (t) => setHand({ link, tab: t }) }
}
export const FAULT_KINDS = ['leak', 'no_run', 'low_pressure', 'head_blocked', 'valve_stuck', 'controller_alarm', 'flow_alarm', 'other'] as const

/** M16-L1: the site's constraints (pressure windows, pump limits, the fontainier's rules), a text whoever reads the water sees where the runs are typed and
 *  the faults are dealt with; nothing when the club wrote none. Written on Paramètres › Contraintes du site. Its place is the page's header (under the
 *  boundary banner, `Water.tsx`, placed at Z's close), so it is on every tab once. */
export function ConstraintsNote() {
  const { t } = useTranslation()
  const water = useWater()
  const text = useMemo(() => siteConstraints(water.profiles), [water.profiles])
  if (!text) return null
  return (
    <div className="card flex flex-col gap-1" data-testid="water-constraints">
      <h3 className="font-bold">{t('water.constraints.title')}</h3>
      <p className="whitespace-pre-wrap" data-testid="water-constraints-text">{text}</p>
      <p className="meta">{t('water.constraints.readHelp')}</p>
    </div>
  )
}
